import { computed, unref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  isCustomAttrSectionKey,
  LEGACY_CUSTOM_ATTR_SECTION_KEY,
} from './eventSectionKeys';

export const DEFAULT_EVENT_SHOW_SECTIONS_ORDER = Object.freeze([
  'key_info',
  'general_info',
  LEGACY_CUSTOM_ATTR_SECTION_KEY,
  'pricing',
  'quotes',
  'documents',
  'floorplans',
  'schedule',
]);

/** Main-column fixed sections that can be soft-hidden (excludes sidebar key_info). */
export const MAIN_COLUMN_FIXED_SECTION_KEYS = Object.freeze([
  'general_info',
  'pricing',
  'quotes',
  'documents',
  'floorplans',
  'schedule',
]);


const UI_SETTINGS_KEY = 'event_show_sections_order';

const FIXED_SECTION_KEYS = DEFAULT_EVENT_SHOW_SECTIONS_ORDER.filter(
  key => key !== LEGACY_CUSTOM_ATTR_SECTION_KEY
);

const expandCustomAttrAnchor = (keys, visibleCustomAttrKeys) => {
  const expanded = [];
  keys.forEach(key => {
    if (key === LEGACY_CUSTOM_ATTR_SECTION_KEY) {
      expanded.push(...visibleCustomAttrKeys);
    } else {
      expanded.push(key);
    }
  });
  return expanded;
};

const buildDefaultOrder = (visibility, visibleCustomAttrKeys) => {
  const ordered = [];

  DEFAULT_EVENT_SHOW_SECTIONS_ORDER.forEach(key => {
    if (key === LEGACY_CUSTOM_ATTR_SECTION_KEY) {
      visibleCustomAttrKeys.forEach(customKey => {
        if (visibility[customKey]) ordered.push(customKey);
      });
      return;
    }

    if (visibility[key]) ordered.push(key);
  });

  return ordered;
};

const insertMissingCustomKeys = (
  ordered,
  visibleCustomAttrKeys,
  visibility
) => {
  const missing = visibleCustomAttrKeys.filter(
    key => visibility[key] && !ordered.includes(key)
  );
  if (!missing.length) return ordered;

  const firstCustomIdx = ordered.findIndex(isCustomAttrSectionKey);
  if (firstCustomIdx >= 0) {
    const lastCustomIdx = ordered.findLastIndex(isCustomAttrSectionKey);
    ordered.splice(lastCustomIdx + 1, 0, ...missing);
    return ordered;
  }

  const generalInfoIdx = ordered.indexOf('general_info');
  const insertAt = generalInfoIdx >= 0 ? generalInfoIdx + 1 : ordered.length;
  ordered.splice(insertAt, 0, ...missing);
  return ordered;
};

const buildOrderFromSaved = (saved, visibility, visibleCustomAttrKeys) => {
  const expanded = expandCustomAttrAnchor(saved, visibleCustomAttrKeys);
  const ordered = expanded.filter(key => visibility[key]);

  FIXED_SECTION_KEYS.forEach(key => {
    if (!visibility[key] || ordered.includes(key)) return;

    const defaultIndex = DEFAULT_EVENT_SHOW_SECTIONS_ORDER.indexOf(key);
    let insertAt = ordered.length;
    for (let i = defaultIndex - 1; i >= 0; i -= 1) {
      const previousKey = DEFAULT_EVENT_SHOW_SECTIONS_ORDER[i];
      if (previousKey === LEGACY_CUSTOM_ATTR_SECTION_KEY) continue;
      const previousIndex = ordered.indexOf(previousKey);
      if (previousIndex >= 0) {
        insertAt = previousIndex + 1;
        break;
      }
    }
    ordered.splice(insertAt, 0, key);
  });

  return insertMissingCustomKeys(ordered, visibleCustomAttrKeys, visibility);
};

export const reconcileSectionOrder = (currentKeys, targetKeys) => {
  const targetSet = new Set(targetKeys);
  const result = currentKeys.filter(key => targetSet.has(key));
  const resultSet = new Set(result);

  targetKeys.forEach((key, index) => {
    if (resultSet.has(key)) return;

    let insertAt = result.length;
    for (let i = index - 1; i >= 0; i -= 1) {
      const previousIndex = result.indexOf(targetKeys[i]);
      if (previousIndex >= 0) {
        insertAt = previousIndex + 1;
        break;
      }
    }

    result.splice(insertAt, 0, key);
    resultSet.add(key);
  });

  return result;
};

export function useEventSectionOrder({
  visibleCustomAttrSectionKeys,
  hiddenSectionKeys,
}) {
  const { uiSettings, updateUISettings } = useUISettings();

  const sectionVisibility = computed(() => {
    const hidden = new Set(unref(hiddenSectionKeys) || []);
    const visibility = {
      key_info: true,
      event_manager: false,
      collaborators: false,
      general_info: !hidden.has('general_info'),
      pricing: !hidden.has('pricing'),
      quotes: !hidden.has('quotes'),
      documents: !hidden.has('documents'),
      floorplans: !hidden.has('floorplans'),
      schedule: !hidden.has('schedule'),
    };


    const customKeys = unref(visibleCustomAttrSectionKeys) || [];
    customKeys.forEach(key => {
      visibility[key] = true;
    });

    return visibility;
  });

  const orderedSectionKeys = computed(() => {
    const visibility = sectionVisibility.value;
    const visibleCustomAttrKeys = unref(visibleCustomAttrSectionKeys) || [];
    const saved = uiSettings.value[UI_SETTINGS_KEY];

    if (!saved?.length) {
      return buildDefaultOrder(visibility, visibleCustomAttrKeys);
    }

    return buildOrderFromSaved(saved, visibility, visibleCustomAttrKeys);
  });

  const saveSectionOrder = keys => {
    updateUISettings({ [UI_SETTINGS_KEY]: keys });
  };

  return {
    orderedSectionKeys,
    sectionVisibility,
    saveSectionOrder,
    FIXED_SECTION_KEYS,
  };
}
